interface WordMergeErrorInfo {
    code?: string;
    message?: string;
    location?: string;
}

export const MERGE_STEPS = [
    "parse_current",
    "parse_backend",
    "preflight_merged_package",
    "insert_merged",
    "insert_merged_and_revert",
] as const;

export type MergeStep = (typeof MERGE_STEPS)[number];

/**
 * Error subclass that captures which merge step failed via {@link mergeStep}.
 * OOXML payloads are deliberately *not* sent to Sentry to avoid leaking
 * customer document content; the in-process auto-repair workflow consumes
 * them via the redline backend.
 */
export class OoxmlMergeError extends Error {
    mergeStep: MergeStep;
    failedOoxml: string;
    currentRangeOoxml: string;
    wordError?: WordMergeErrorInfo;
    /** Dioptra paragraph bookmarks the caller resolved `range` from.
     *  Stamped by `applyOoxmlChanges` so the agent recovery fallback can
     *  re-locate the paragraphs via `getBookmarkRangeOrNullObject`. */
    bookmarks?: string[];

    constructor(message: string, details: {
        mergeStep: MergeStep;
        cause?: Error;
        failedOoxml: string;
        currentRangeOoxml: string;
        wordError?: WordMergeErrorInfo;
        bookmarks?: string[];
    }) {
        super(message, { cause: details.cause });
        this.mergeStep = details.mergeStep;
        this.wordError = details.wordError;
        this.bookmarks = details.bookmarks;
        Object.defineProperties(this, {
            failedOoxml: { value: details.failedOoxml, enumerable: false, writable: true },
            currentRangeOoxml: { value: details.currentRangeOoxml, enumerable: false, writable: true },
        });
    }
}

import { PackageBinaryParts } from "@common/utils/redline/ooxml/package-binary-parts";
import { preserveParagraphBookmarks } from "./paragraph-bookmark-transplant";

function getPartByName(doc: Document, name: string): Element | undefined {
    return Array.from(doc.getElementsByTagName("pkg:part")).find(p => p.getAttribute("pkg:name") === name);
}

export function redlineCarriesCommentThreads(redlineOoxml: string): boolean {
    return redlineOoxml.includes("/word/commentsExtended.xml");
}

function normalizedParaId(entry: Element): string | null {
    return entry.getAttribute("w15:paraId")?.toUpperCase() ?? null;
}

/**
 * Puts each comment's resolved state, as the live document holds it, onto the merged package.
 *
 * WHY the live read is parsed without its embedded fonts
 *   The live read is the whole body of the document, and a document that embeds its fonts
 *   hands it as millions of characters of font parts that hold no comment. Parsed with them,
 *   every entry that carries comment threads parses them again
 *   (`PackageBinaryParts.withoutEmbeddedFonts`).
 *
 * EXAMPLE
 *   live read 10,678,456 characters, 10 font parts among them -> the font parts are not parsed
 */
export function restoreLiveResolveState(mergedOoxml: string, liveDocumentOoxml: string): string {
    const liveDocumentWithoutEmbeddedFonts = PackageBinaryParts.withoutEmbeddedFonts(liveDocumentOoxml);
    const liveResolveStateByParaId = new Map(
        Array.from(new DOMParser().parseFromString(liveDocumentWithoutEmbeddedFonts, "text/xml").getElementsByTagName("w15:commentEx"))
            .map((entry) => [normalizedParaId(entry), entry.getAttribute("w15:done") ?? "0"]),
    );
    const mergedDoc = new DOMParser().parseFromString(mergedOoxml, "text/xml");
    for (const entry of Array.from(mergedDoc.getElementsByTagName("w15:commentEx"))) {
        const liveResolved = liveResolveStateByParaId.get(normalizedParaId(entry));
        if (liveResolved !== undefined) {
            entry.setAttribute("w15:done", liveResolved);
        }
    }
    return new XMLSerializer().serializeToString(mergedDoc);
}

/**
 * The current range's package with the new package's parts laid over it, the package `insertOoxml` writes.
 *
 * WHY both packages lose their embedded fonts before they are merged
 *   The current range is Word's own read, and a document that embeds its fonts reads them
 *   into every range: 8.3 to 9.0 million characters for one paragraph. Merged, they would
 *   stay in the package that is written, and `insertOoxml` would take them in again. The
 *   document keeps its own embedded fonts whatever the write carries
 *   (`PackageBinaryParts.withoutEmbeddedFonts`).
 *
 * EXAMPLE
 *   current 8,958,297 characters, new 9,827,098 characters, both with 10 font parts
 *   -> a merged package with no font part in it
 */
export function mergeOoxmlPackages(currentOoxml: string, newOoxml: string): string {
    const currentPackageWithoutEmbeddedFonts = PackageBinaryParts.withoutEmbeddedFonts(currentOoxml);
    const newPackageWithoutEmbeddedFonts = PackageBinaryParts.withoutEmbeddedFonts(newOoxml);
    const parser = new DOMParser();
    const currentDoc = parser.parseFromString(currentPackageWithoutEmbeddedFonts, "text/xml");
    const currentParseError = currentDoc.querySelector('parsererror');
    if (currentParseError) {
        throw new OoxmlMergeError('Failed to parse current OOXML: ' + (currentParseError.textContent || '').slice(0, 200), {
            mergeStep: 'parse_current',
            failedOoxml: newPackageWithoutEmbeddedFonts,
            currentRangeOoxml: currentPackageWithoutEmbeddedFonts,
        });
    }

    const newDoc = parser.parseFromString(newPackageWithoutEmbeddedFonts, "text/xml");
    const newParseError = newDoc.querySelector('parsererror');
    if (newParseError) {
        throw new OoxmlMergeError('Failed to parse backend OOXML: ' + (newParseError.textContent || '').slice(0, 200), {
            mergeStep: 'parse_backend',
            failedOoxml: newPackageWithoutEmbeddedFonts,
            currentRangeOoxml: currentPackageWithoutEmbeddedFonts,
        });
    }

    for (const newPart of Array.from(newDoc.getElementsByTagName("pkg:part"))) {
        const partName = newPart.getAttribute("pkg:name");
        if (!partName) continue;

        const existingPart = getPartByName(currentDoc, partName);
        const importedNode = currentDoc.importNode(newPart, true) as Element;
        if (existingPart) {
            const supersededParagraphs = Array.from(existingPart.getElementsByTagName("w:p"));
            existingPart.parentNode?.replaceChild(importedNode, existingPart);
            preserveParagraphBookmarks(
                supersededParagraphs,
                Array.from(importedNode.getElementsByTagName("w:p")),
                currentDoc,
            );
        } else {
            currentDoc.getElementsByTagName("pkg:package")[0]?.appendChild(importedNode);
        }
    }

    return new XMLSerializer().serializeToString(currentDoc);
}
